import { useIntl } from 'react-intl';
import {
  IconPlayerSkipBack,
  IconPlayerSkipForward,
} from '@rossum/ui/icons/tabler';
import { Button, Stack, SvgIcon } from '@rossum/ui/material';
import { SidebarAction, SidebarActionBar } from '../../document-sidebar';

type SplitSidebarFooterProps = {
  onConfirm?: () => void;
  confirmInProgress?: boolean;
  hasChanges?: boolean;
  onNavigatePreviousAnnotation?: () => void;
  onNavigateNextAnnotation?: () => void;
};

const noop = () => {};

/**
 * Footer of the splitting sidebar: stack navigation plus the confirm action.
 *
 * `onConfirm` and `confirmInProgress` are wired by MAT-1965 (S7a). The
 * annotation-stack navigation handlers are still unset and render disabled
 * until that feature lands.
 */
export const SplitSidebarFooter = ({
  onConfirm,
  confirmInProgress,
  hasChanges,
  onNavigatePreviousAnnotation,
  onNavigateNextAnnotation,
}: SplitSidebarFooterProps) => {
  const intl = useIntl();

  const actions: SidebarAction[] = [
    {
      id: 'next-annotation',
      label: intl.formatMessage({
        id: 'features.documentSplit.sidebar.footer.nextAnnotation',
      }),
      icon: (
        <SvgIcon>
          <IconPlayerSkipForward />
        </SvgIcon>
      ),
      onClick: onNavigateNextAnnotation ?? noop,
      disabled: !onNavigateNextAnnotation,
      dataCy: 'split-sidebar-next-annotation',
    },
    {
      id: 'previous-annotation',
      label: intl.formatMessage({
        id: 'features.documentSplit.sidebar.footer.previousAnnotation',
      }),
      icon: (
        <SvgIcon>
          <IconPlayerSkipBack />
        </SvgIcon>
      ),
      onClick: onNavigatePreviousAnnotation ?? noop,
      disabled: !onNavigatePreviousAnnotation,
      dataCy: 'split-sidebar-previous-annotation',
    },
  ];

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        py: 1,
        px: 1,
        width: '100%',
        borderTop: '1px solid',
        borderColor: theme => theme.palette.divider,
        backgroundColor: theme => theme.palette.background.paper,
      }}
      data-cy="split-sidebar-footer"
    >
      <SidebarActionBar actions={actions}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          data-cy="document-edit-confirm-btn"
          onClick={() => onConfirm?.()}
          // TODO: also disable while required edit values are missing
          // (legacy `canConfirm` in `EditDocumentHeader`).
          disabled={!onConfirm || confirmInProgress || !hasChanges}
        >
          {intl.formatMessage({ id: 'containers.editDocument.confirm' })}
        </Button>
      </SidebarActionBar>
    </Stack>
  );
};
